import chunk from 'lodash.chunk';
import groupBy from 'lodash.groupby';
import intersection from 'lodash.intersection';
import { action, observable } from 'mobx';
import i18n from '../../../../i18n';
import { SEGMENT_OTHERS_INDICATOR } from '../../../common/api/api-interfaces';
import { dateManagerService } from '../../../common/date-manager/date-manager-service';
import { ExportData } from '../../../common/export-store';
import {
  MetricCategoryId,
  MetricGroupId,
  MetricId,
} from '../../../common/graphql/generated/graphql-sdk';
import { localStore } from '../../../common/local-store';
import { MetricStore } from '../../../common/metrics/metric-store';
import { getMetricSegmentationService } from '../../../common/metrics/services/getMetricServices';
import { formatHierarchicalChartLabelsToTicks } from '../../../common/utilFunctions/formatters';
import { Alias } from '../../../common/v2/api/alias/types';
import {
  getAliasForMetricCategoryId,
  getAliasForMetricGroupId,
} from '../../../common/v2/api/alias/utils';
import { Hasher } from '../../../common/v2/api/hasher';
import {
  HasDependencies,
  hashString,
  VersionId,
} from '../../../common/v2/api/types';
import {
  Filter,
  Segment,
} from '../../../common/v2/common/components/filter/filterbar/types';
import { translateLabel } from '../../../common/v2/common/utils';
import { Languages } from '../../../common/v2/constants';
import {
  LegacyDomainScopedStore,
  LegacyStore,
} from '../../../common/v2/context/legacy-types';
import { getMomentDate } from '../../../common/v2/date/date-utils';
import { DataFields, DataTypes } from '../../../common/v2/types';
import { getModifiedFiltersAndSegments } from './BalanceSheetSegmentationRows';
import {
  benchmarkHeaderCellValGetter,
  fixedHeaderCellValGetter,
  getCellValForSegmentMeasure,
  getFormattedValForBenchmarkHeader,
  getFormattedValForFixedHeader,
  getFormattedValForTimeHeader,
  timeHeaderCellValGetter,
} from './services/pbs-utils';
import {
  getDisplayMetricCategoryName,
  getDisplayMetricGroupName,
  getExportRowDataObjKeyTranslation,
  getPbsSegmentMeasureLabel,
} from './translations';
import {
  BalanceSheetStoreConfig,
  BalanceSheetTableService,
  PbsBenchmarkTypes,
  PbsCategoryDropdownItem,
  PbsHierarchicalValue,
  PbsMetricChart,
  PbsMetricChartMeasures,
  PbsSegmentationServiceInputs,
  PbsSegmentationServiceResponse,
  PbsServiceResponseData,
  SegmentValTypes,
} from './types';

export enum ExportRowDataObjKeys {
  METRIC_NAME = 'metricName',
  SEGMENT_LEVEL_1 = 'segmentLevel1',
  SEGMENT_LEVEL_2 = 'segmentLevel2',
  MEASURE = 'measure',
  PERCENTAGE_OF_TOTAL_INDICATOR = 'percentOfTotalIndicator',
}

interface ExportRowDataObj {
  [ExportRowDataObjKeys.METRIC_NAME]: string;
  [ExportRowDataObjKeys.SEGMENT_LEVEL_1]: string;
  [ExportRowDataObjKeys.SEGMENT_LEVEL_2]: string;
  [ExportRowDataObjKeys.MEASURE]: string;
  [key: string]: string;
}

type MetricIdsToSelectedmetricMap = Partial<Record<MetricGroupId, MetricId>>;

export class BalanceSheetStore implements HasDependencies {
  public get selectedPbsValues(): PbsHierarchicalValue[] {
    return this.dropdownValues
      ?.map(({ value, subItems }) => {
        return {
          value,
          subItems: subItems
            .filter((sub) => sub.selected)
            .map((sub) => sub.value),
        };
      })
      .filter(({ subItems }) => subItems.length > 0);
  }

  public get selectedMetricIds(): MetricGroupId[] {
    const selectedPbsItems = this.selectedPbsValues.flatMap((v) => v.subItems);
    return selectedPbsItems;
  }

  public rowInfoLink;
  public dropdownInfoLink;
  public metricsPageLink;

  public configuredMetricGroups: MetricGroupId[];

  public balanceSheetTableService: BalanceSheetTableService;

  @observable
  public dropdownValues: PbsCategoryDropdownItem[] = [];

  @observable
  public segmentMeasures: SegmentValTypes[] = [
    SegmentValTypes.COUNT,
    SegmentValTypes.PERCENTAGE_OF_TOTAL,
  ];

  @observable
  public segmentedSubVal: MetricGroupId | null = null;

  @observable
  public metricChart: PbsMetricChart | null = null;

  @observable
  public secondaryMetricChart: PbsMetricChart | null = null;

  @observable
  public metricChartMeasure: PbsMetricChartMeasures | null = null;

  @observable
  public secondaryMetricChartMeasure: PbsMetricChartMeasures | null = null;

  @observable
  public selectedPbsBenchmarkType: PbsBenchmarkTypes = PbsBenchmarkTypes.YOY;

  @observable
  public metricsToSelectedMetricIdsMap: MetricIdsToSelectedmetricMap | null =
    null;

  @observable
  public exportProcessing = false;

  private CHECKED_PBS_VALUES_KEY;
  private ITEMS_TO_SELECTED_DIMENSIONS_KEY;
  private SELECTED_SEGMENT_1_MEASURE_KEY = 'SELECTED_SEGMENT_1_MEASURE';
  private SELECTED_SEGMENT_2_MEASURE_KEY = 'SELECTED_SEGMENT_2_MEASURE';

  // tslint:disable-next-line: member-ordering
  @observable
  public selectedSegmentLevel1Measure: SegmentValTypes =
    (localStore.get(this.SELECTED_SEGMENT_1_MEASURE_KEY) as SegmentValTypes) ??
    SegmentValTypes.COUNT;

  // tslint:disable-next-line: member-ordering
  @observable
  public selectedSegmentLevel2Measure: SegmentValTypes =
    (localStore.get(this.SELECTED_SEGMENT_2_MEASURE_KEY) as SegmentValTypes) ??
    SegmentValTypes.COUNT;

  private initialCheckedPbsValues: MetricGroupId[];

  @observable
  private checkedPbsValues: MetricGroupId[] = [];

  private getRawDropdownValuesFromMetricGroups = (
    metricGroups: MetricGroupId[]
  ): PbsHierarchicalValue[] => {
    const allowedMetricsGroupedByCategory = groupBy(metricGroups, (m) =>
      this.metricStore.getCategoryForMetricGroup(m)
    );
    const { metricGroupToSortPositionMap, metricCategoryToSortPositionMap } =
      this.metricStore;
    const dropdownFormattedMetrics: PbsHierarchicalValue[] = Object.entries(
      allowedMetricsGroupedByCategory
    )
      .map(([category, metrics]) => {
        return {
          value: category,
          subItems: metrics.sort((a, b) => {
            return (
              metricGroupToSortPositionMap[a] - metricGroupToSortPositionMap[b]
            );
          }),
        } as {
          value: MetricCategoryId;
          subItems: MetricGroupId[];
        };
      })
      .sort((a, b) => {
        return (
          metricCategoryToSortPositionMap[a.value] -
          metricCategoryToSortPositionMap[b.value]
        );
      });
    return dropdownFormattedMetrics;
  };

  public loadDropdownValues = async () => {
    const domainAllowedMetricGroups = Array.from(
      this.metricStore.domainAllowedMetricGroupsSet
    );
    const permissionsAllowedMetricGroups =
      this.metricStore.getPermissionsAllowedMetricGroups();
    const finalAllowedMetricGroups = intersection(
      this.configuredMetricGroups,
      domainAllowedMetricGroups,
      permissionsAllowedMetricGroups
    );
    const rawDropdownValues = this.getRawDropdownValuesFromMetricGroups(
      finalAllowedMetricGroups
    );
    const formattedDropdownValues = rawDropdownValues.map(
      ({ value, subItems }) => {
        const metricCategoryIdAlias = getAliasForMetricCategoryId(
          this.aliases,
          this.selectedLanguage
        )(value);
        const allSubItemsDefaultSelected = subItems.every((v) =>
          this.checkedPbsValues.includes(v)
        );
        return {
          value,
          selected: allSubItemsDefaultSelected,
          isExpanded: true,
          label: getDisplayMetricCategoryName(
            value,
            metricCategoryIdAlias,
            this.metricStore
          ),
          subItems: subItems.map((subVal) => {
            const metricGroupIdAlias = getAliasForMetricGroupId(
              this.aliases,
              this.selectedLanguage
            )(subVal);
            return {
              value: subVal,
              selected: this.checkedPbsValues.some(
                (pbsVal) => pbsVal === subVal
              ),
              label: getDisplayMetricGroupName(
                subVal,
                metricGroupIdAlias,
                this.metricStore
              ),
            };
          }),
        };
      }
    );
    this.setDropdownValues(formattedDropdownValues);
    this.loadDefaultSelectedPbsMeasures();
  };

  // This is temporary until migration for all clients is done
  private isValidCheckedPbsValues = (checkedPbsValuesString: string) => {
    const checkedPbsValues: string[] = JSON.parse(checkedPbsValuesString);
    const metricGroupIdsSet = this.metricStore.domainAllowedMetricGroupsSet;
    const permissionsAllowedMetricGroups = new Set(
      this.metricStore.getPermissionsAllowedMetricGroups()
    );
    return (
      checkedPbsValues.length &&
      checkedPbsValues.every(
        (v) =>
          metricGroupIdsSet.has(v as MetricGroupId) &&
          permissionsAllowedMetricGroups.has(v as MetricGroupId)
      )
    );
  };

  // Filter out any forbidden metrics from the cached values
  private filterAllowedMetricsFromCache = (
    cachedMetrics: MetricGroupId[]
  ): MetricGroupId[] => {
    const domainAllowedMetricGroups =
      this.metricStore.domainAllowedMetricGroupsSet;
    const permissionsAllowedMetricGroups = new Set(
      this.metricStore.getPermissionsAllowedMetricGroups()
    );

    return cachedMetrics.filter(
      (metricId) =>
        domainAllowedMetricGroups.has(metricId) &&
        permissionsAllowedMetricGroups.has(metricId)
    );
  };

  constructor(
    readonly metricStore: MetricStore,
    readonly aliases: Alias[],
    readonly selectedLanguage: Languages,
    readonly config: BalanceSheetStoreConfig
  ) {
    const {
      name,
      initialCheckedPbsValues,
      dashboardLinkKey,
      balanceSheetTableService,
      configuredMetricGroups,
    } = config;
    this.CHECKED_PBS_VALUES_KEY = `${name}_CHECKED_PBS_VALUES`;
    this.ITEMS_TO_SELECTED_DIMENSIONS_KEY = `${name}_ITEMS_TO_SELECTED_DIMENSIONS`;
    this.initialCheckedPbsValues = initialCheckedPbsValues;
    const checkedPbsValues = localStore.get(this.CHECKED_PBS_VALUES_KEY);
    if (checkedPbsValues && this.isValidCheckedPbsValues(checkedPbsValues)) {
      const parsedValues = JSON.parse(checkedPbsValues);
      // Filter out any metrics that are no longer allowed due to permission changes
      const allowedValues = this.filterAllowedMetricsFromCache(parsedValues);
      // If filtering removed all metrics, fall back to initial values
      this.checkedPbsValues =
        allowedValues.length > 0 ? allowedValues : this.initialCheckedPbsValues;
      // Update localStorage to reflect the filtered values
      localStore.set(
        this.CHECKED_PBS_VALUES_KEY,
        JSON.stringify(this.checkedPbsValues)
      );
    } else {
      this.checkedPbsValues = this.initialCheckedPbsValues;
      localStore.set(
        this.CHECKED_PBS_VALUES_KEY,
        JSON.stringify(this.initialCheckedPbsValues)
      );
    }

    this.metricsPageLink = `/dashboard/${dashboardLinkKey}/2`;
    this.rowInfoLink = `/dashboard/${dashboardLinkKey}/3`;
    this.dropdownInfoLink = `/dashboard/${dashboardLinkKey}/4`;

    this.balanceSheetTableService = balanceSheetTableService;

    this.configuredMetricGroups = configuredMetricGroups;
  }

  public getDeps = () => [
    hashString([
      ...this.metricStore.getDeps(),
      Hasher.hash(this.aliases),
      Hasher.hash(this.selectedLanguage),
      Hasher.hash(this.config),
    ]),
  ];

  public loadDefaultSelectedPbsMeasures = () => {
    const itemsToSelectedMeasuresMap = localStore.get(
      this.ITEMS_TO_SELECTED_DIMENSIONS_KEY
    );

    const defaultMap = Object.fromEntries(
      this.metricStore.allMetricGroupDetailsWithAllowedSortedMetricIds
        .filter((m) => m.dimensions.length)
        .map((r) => [r.id, r.dimensions[0].id])
    );
    if (itemsToSelectedMeasuresMap) {
      const localStoreItemToSelectedMeasureMap = JSON.parse(
        itemsToSelectedMeasuresMap
      );
      const cleanedLocalStoreItemToSelectedMeasureMap = Object.fromEntries(
        Object.entries(localStoreItemToSelectedMeasureMap)
          .map(([metricGroup, metric]) => {
            let allowedMetricIds: MetricId[] = [];
            try {
              allowedMetricIds = this.metricStore
                .getAllowedSortedMetricIdsForMetricGroup(
                  metricGroup as MetricGroupId
                )
                .map((d) => d.id);
            } catch (e) {
              // must be invalid local store config
              // noop
            }
            if (allowedMetricIds.includes(metric as MetricId)) {
              return [metricGroup, metric];
            } else {
              return null;
            }
          })
          .filter(Boolean) as [MetricGroupId, MetricId][]
      );
      this.metricsToSelectedMetricIdsMap = {
        ...defaultMap,
        ...cleanedLocalStoreItemToSelectedMeasureMap,
      };
    } else {
      this.metricsToSelectedMetricIdsMap = defaultMap;
    }
    localStore.set(
      this.ITEMS_TO_SELECTED_DIMENSIONS_KEY,
      JSON.stringify(this.metricsToSelectedMetricIdsMap)
    );
  };

  @action
  public setSegmentedSubVal = (value: MetricGroupId | null) => {
    this.segmentedSubVal = value;
  };

  @action
  public setSelectedSegmentLevel1Measure = (measure: SegmentValTypes) => {
    this.selectedSegmentLevel1Measure = measure;
    localStore.set(this.SELECTED_SEGMENT_1_MEASURE_KEY, measure);
  };

  @action
  public setSelectedSegmentLevel2Measure = (measure: SegmentValTypes) => {
    this.selectedSegmentLevel2Measure = measure;
    localStore.set(this.SELECTED_SEGMENT_2_MEASURE_KEY, measure);
  };

  @action
  public isSegmentationPercAllowed = (metricId: MetricId) => {
    return this.metricStore.getMetricDetailsFromMetricId(metricId)
      .segmentationPercAllowed;
  };

  @action
  public setMetricChart = (pbsMetricObject: PbsMetricChart) => {
    this.metricChart = pbsMetricObject;

    this.setMetricChartMeasure(pbsMetricObject?.metricIds[0]);
  };

  @action
  public setMetricChartMeasure = (
    pbsMetricMeasureObject: PbsMetricChartMeasures
  ) => {
    this.metricChartMeasure = pbsMetricMeasureObject;
  };

  @action
  public toggleSecondaryMetricChart = (pbsMetricObject: PbsMetricChart) => {
    if (
      this.secondaryMetricChart?.metricGroupId === pbsMetricObject.metricGroupId
    ) {
      this.secondaryMetricChart = null;
      this.setSecondaryMetricChartMeasure(null);
    } else {
      this.secondaryMetricChart = pbsMetricObject;
      this.setSecondaryMetricChartMeasure(pbsMetricObject?.metricIds[0]);
    }
  };

  @action
  public setSecondaryMetricChartMeasure = (
    pbsMetricMeasureObject: PbsMetricChartMeasures | null
  ) => {
    this.secondaryMetricChartMeasure = pbsMetricMeasureObject;
  };

  // default metric to first subItem
  @action
  public loadMetricChart = () => {
    const value = this.dropdownValues[0]?.subItems[0]?.value;

    // Handle case where no metrics are available (all forbidden or none configured)
    if (!value) {
      console.warn(
        'No metrics available to load in PBS chart. This may happen if all previously selected metrics are now forbidden.'
      );
      return;
    }

    try {
      const metricIds = this.metricStore
        .getAllowedSortedMetricIdsForMetricGroup(value)
        .map((m) => {
          return {
            label: this.metricStore.getTranslatedMetricIdName(m.id),
            value: m.id,
            key: m.id,
          };
        });

      this.setMetricChart({
        label: this.dropdownValues[0]?.subItems[0]?.label,
        metricGroupId: value,
        metricIds,
      });

      this.setMetricChartMeasure(metricIds[0]);
    } catch (e) {
      console.error(
        `Failed to load metric chart for metric group ${value}. This may happen if the metric is forbidden or invalid.`,
        e
      );
      // Silently fail - the UI should handle the missing chart gracefully
    }
  };

  @action
  public setSelectedPbsBenchmarkType = (newType: PbsBenchmarkTypes) =>
    (this.selectedPbsBenchmarkType = newType);

  @action
  public setDropdownValues = (newObjArray: PbsCategoryDropdownItem[]) => {
    this.dropdownValues = newObjArray;
    const newCheckedPbsValues = newObjArray
      .flatMap((item) => item.subItems.filter((i) => i.selected))
      .map((item) => item.value);
    localStore.set(
      this.CHECKED_PBS_VALUES_KEY,
      JSON.stringify(newCheckedPbsValues)
    );
  };

  @action
  public togglePbsCategoryIsExpanded = (headerVal: MetricCategoryId) => {
    const toggleObj = this.dropdownValues?.find(
      (obj) => obj.value === headerVal
    );
    if (toggleObj) {
      toggleObj.isExpanded = !toggleObj.isExpanded;
    }
  };

  @action
  public setSelectedMeasureOnPbsItem = (
    metricGroupId: MetricGroupId,
    metricId: MetricId
  ) => {
    if (this.metricsToSelectedMetricIdsMap) {
      this.metricsToSelectedMetricIdsMap[metricGroupId] = metricId;
      localStore.set(
        this.ITEMS_TO_SELECTED_DIMENSIONS_KEY,
        JSON.stringify(this.metricsToSelectedMetricIdsMap)
      );
    }
  };

  @action
  public toggleExportProcessing = () =>
    (this.exportProcessing = !this.exportProcessing);

  public exportPbsTable = async (
    exportDocumentType: 'csv' | 'xlsx',
    exportPrefix: string,
    formatData: boolean,
    filters: Filter[],
    segmentationLevel1: Segment[],
    segmentationLevel2: Segment[],
    latestVersions: Partial<Record<DataTypes, VersionId>>,
    legacyStores: LegacyStore,
    legacyDomainScopedStores: LegacyDomainScopedStore
  ) => {
    const {
      cachedMasterDataApiService,
      exportStore,
      loadingStateStore,
      timeSliderStore,
      benchmarkStore,
    } = legacyDomainScopedStores;
    this.toggleExportProcessing();
    loadingStateStore.loadStarted();
    // loading state store loadStarted is needed as without this, when there is some time lag
    // between different queries needed for export(time lag could be due to some data processing),
    // the background loading overlay disappears for a brief second and then reappears when
    // the next query is sent. To avoid that flickering, we make it only stop once the export has finished.
    exportStore.setExportDocumentType(exportDocumentType);
    const { timeSliderConfig } = timeSliderStore;
    const { selectedBenchmark } = benchmarkStore;
    const inputs = {
      timeSliderConfig,
      selectedPbsValues: this.selectedPbsValues,
      selectedBenchmark,
    };
    const tableData = await this.balanceSheetTableService(
      inputs,
      filters,
      latestVersions,
      legacyStores,
      legacyDomainScopedStores
    );
    const numSegmentsLoadedTogether = 5;
    const selectedMetrics = this.selectedPbsValues.flatMap((v) => v.subItems);
    const metricChunks = chunk(selectedMetrics, numSegmentsLoadedTogether);
    const getSegmentDataForPbsItems = async (pbsItems: MetricGroupId[]) => {
      return Promise.all(
        pbsItems.map(async (item) => {
          const segmentationService = getMetricSegmentationService(
            item,
            this.metricStore,
            cachedMasterDataApiService
          );
          const {
            finalFilters,
            finalSegmentationLevel1,
            finalSegmentationLevel2,
          } = getModifiedFiltersAndSegments(item, {
            filters,
            segmentationLevel1,
            segmentationLevel2,
            metricStore: this.metricStore,
          });
          const serviceInputs: PbsSegmentationServiceInputs = {
            segmentationLevel1: finalSegmentationLevel1,
            segmentationLevel2: finalSegmentationLevel2,
            timeSliderConfig,
            selectedBenchmark,
          };
          return segmentationService(
            serviceInputs,
            finalFilters,
            latestVersions,
            legacyStores,
            legacyDomainScopedStores
          );
        })
      );
    };
    const allSegmentsData: Array<PbsSegmentationServiceResponse | null> = [];
    if (segmentationLevel1.length) {
      for (const metricChunk of metricChunks) {
        // Sending segment requests in batches to the backend, one after the other,
        // to avoid overloading the backend with too many requests all at once
        const segData = await getSegmentDataForPbsItems(metricChunk);
        allSegmentsData.push(...segData);
      }
    }

    const { timeHeaderObjs, fixedHeaderObjs, benchmarkHeaderObjs } = tableData;
    const pbsRows = tableData.tableData.rowGroups.flatMap((r) => r.rows);
    const getDataForSegment = (
      data: PbsServiceResponseData,
      totalData: PbsServiceResponseData,
      selectedMeasure: MetricId,
      isPercentage: boolean
    ) => {
      const timeHeaderCellVals = Object.fromEntries(
        timeHeaderObjs.map(({ value: timeHeaderVal }) => {
          const val = formatData
            ? getFormattedValForTimeHeader(
                timeHeaderVal,
                selectedMeasure,
                data,
                totalData,
                isPercentage,
                this.metricStore
              )
            : getCellValForSegmentMeasure(
                (dataToUse) =>
                  timeHeaderCellValGetter(
                    dataToUse,
                    timeHeaderVal,
                    selectedMeasure
                  ),
                data,
                totalData,
                isPercentage
              );
          return [timeHeaderVal, val];
        })
      );
      const fixedHeaderCellVals = Object.fromEntries(
        fixedHeaderObjs.map(({ value: fixedHeaderVal }) => {
          const val = formatData
            ? getFormattedValForFixedHeader(
                fixedHeaderVal,
                selectedMeasure,
                data,
                totalData,
                isPercentage,
                this.metricStore
              )
            : getCellValForSegmentMeasure(
                (dataToUse) =>
                  fixedHeaderCellValGetter(
                    dataToUse,
                    fixedHeaderVal,
                    selectedMeasure
                  ),
                data,
                totalData,
                isPercentage
              );
          return [fixedHeaderVal, val];
        })
      );
      const benchmarkHeaderCellVals = Object.fromEntries(
        benchmarkHeaderObjs.map(({ value: benchmarkHeaderVal }) => {
          const cellData = data.benchmarkHeadersData?.[benchmarkHeaderVal];
          if (!cellData) {
            return [benchmarkHeaderVal, ''];
          }
          const val = formatData
            ? getFormattedValForBenchmarkHeader(
                benchmarkHeaderVal,
                selectedMeasure,
                data,
                totalData,
                isPercentage,
                this.metricStore
              )
            : getCellValForSegmentMeasure(
                (dataToUse) =>
                  benchmarkHeaderCellValGetter(
                    dataToUse,
                    benchmarkHeaderVal,
                    selectedMeasure
                  ),
                data,
                totalData,
                isPercentage
              );
          return [benchmarkHeaderVal, val];
        })
      );
      return {
        ...timeHeaderCellVals,
        ...fixedHeaderCellVals,
        ...benchmarkHeaderCellVals,
      };
    };

    const exportRowDataObjs: ExportRowDataObj[] = pbsRows.flatMap((row) => {
      const { metricGroupId, data: dataObj } = row;
      const data = dataObj[metricGroupId];
      const selectedMetricId = this.metricsToSelectedMetricIdsMap?.[
        metricGroupId
      ] as MetricId;
      const alias = getAliasForMetricGroupId(
        this.aliases,
        this.selectedLanguage
      )(metricGroupId);
      const metricName = getDisplayMetricGroupName(
        metricGroupId,
        alias,
        this.metricStore
      );
      const TOTAL_STRING = i18n.t('common:commonValues.misc.total');
      const totalSeg1 = `${TOTAL_STRING} ${getExportRowDataObjKeyTranslation(ExportRowDataObjKeys.SEGMENT_LEVEL_1)}`;
      const totalSeg2 = `${TOTAL_STRING} ${getExportRowDataObjKeyTranslation(ExportRowDataObjKeys.SEGMENT_LEVEL_2)}`;
      const segmentName = totalSeg1;
      const selectedMeasureLabel =
        this.metricStore.getTranslatedMetricIdName(selectedMetricId);
      const isSegmentationPercAllowed =
        this.isSegmentationPercAllowed(selectedMetricId);
      const segmentLevel1Measure = this.selectedSegmentLevel1Measure;
      const segmentLevel2Measure = this.selectedSegmentLevel2Measure;
      const segmentLevel1MeasureLabel =
        segmentLevel1Measure === SegmentValTypes.PERCENTAGE_OF_TOTAL
          ? getPbsSegmentMeasureLabel(segmentLevel1Measure)
          : '';
      const isSegmentLevel1Percentage =
        isSegmentationPercAllowed &&
        segmentLevel1Measure === SegmentValTypes.PERCENTAGE_OF_TOTAL;
      const segmentLevel2MeasureLabel =
        segmentLevel2Measure === SegmentValTypes.PERCENTAGE_OF_TOTAL
          ? getPbsSegmentMeasureLabel(segmentLevel2Measure)
          : '';
      const isSegmentLevel2Percentage =
        isSegmentationPercAllowed &&
        segmentLevel2Measure === SegmentValTypes.PERCENTAGE_OF_TOTAL;

      const rowData: ExportRowDataObj = {
        [ExportRowDataObjKeys.METRIC_NAME]: metricName,
        [ExportRowDataObjKeys.SEGMENT_LEVEL_1]: segmentName,
        [ExportRowDataObjKeys.SEGMENT_LEVEL_2]: '',
        [ExportRowDataObjKeys.MEASURE]: selectedMeasureLabel,
        [ExportRowDataObjKeys.PERCENTAGE_OF_TOTAL_INDICATOR]: '',
        ...getDataForSegment(data, data, selectedMetricId, false),
      };
      const dataForSegments = allSegmentsData.find((d) => {
        const item = Object.keys(d?.responseData ?? {})[0];
        return item === metricGroupId;
      });
      const segRows =
        dataForSegments?.responseData?.[metricGroupId].flatMap((d) => {
          const [segName, segData] = Object.entries(d)[0];
          const defaultSegmentLevel1Name = formatHierarchicalChartLabelsToTicks(
            segName
          ).replace(SEGMENT_OTHERS_INDICATOR, '');
          const segmentLevel1Name = i18n.t(
            translateLabel(defaultSegmentLevel1Name, {
              dataType: segmentationLevel1[0].dataType,
              dataField: segmentationLevel1[0].property as DataFields,
            }) ?? defaultSegmentLevel1Name
          );
          const level1ExportRowDataObj: ExportRowDataObj = {
            [ExportRowDataObjKeys.METRIC_NAME]: metricName,
            [ExportRowDataObjKeys.SEGMENT_LEVEL_1]: segmentLevel1Name,
            [ExportRowDataObjKeys.SEGMENT_LEVEL_2]: totalSeg2,
            [ExportRowDataObjKeys.MEASURE]: selectedMeasureLabel,
            [ExportRowDataObjKeys.PERCENTAGE_OF_TOTAL_INDICATOR]:
              segmentLevel1MeasureLabel,
            ...getDataForSegment(
              segData,
              dataForSegments.totalData,
              selectedMetricId,
              isSegmentLevel1Percentage
            ),
          };

          const level2ExportRowDataObjs: ExportRowDataObj[] = segData.segments
            ? segData.segments.segments.map((d) => {
                const [seg2Name, seg2Data] = Object.entries(d)[0];
                const defaultSegmentLevel2Name =
                  formatHierarchicalChartLabelsToTicks(seg2Name).replace(
                    SEGMENT_OTHERS_INDICATOR,
                    ''
                  );
                const segmentLevel2Name = i18n.t(
                  translateLabel(defaultSegmentLevel2Name, {
                    dataType: segmentationLevel2[0].dataType,
                    dataField: segmentationLevel2[0].property as DataFields,
                  }) ?? defaultSegmentLevel2Name
                );
                if (!segData.segments) {
                  throw new Error(
                    'This should not happen as this part of code only occurs when segData.segments is defined'
                  );
                }
                return {
                  [ExportRowDataObjKeys.METRIC_NAME]: metricName,
                  [ExportRowDataObjKeys.SEGMENT_LEVEL_1]: segmentLevel1Name,
                  [ExportRowDataObjKeys.SEGMENT_LEVEL_2]: segmentLevel2Name,
                  [ExportRowDataObjKeys.MEASURE]: selectedMeasureLabel,
                  [ExportRowDataObjKeys.PERCENTAGE_OF_TOTAL_INDICATOR]:
                    segmentLevel2MeasureLabel,
                  ...getDataForSegment(
                    seg2Data,
                    segData.segments.totalData,
                    selectedMetricId,
                    isSegmentLevel2Percentage
                  ),
                };
              })
            : [];
          return [level1ExportRowDataObj, ...level2ExportRowDataObjs];
        }) ?? [];
      return [rowData, ...segRows];
    });
    const allColumns = [
      ExportRowDataObjKeys.METRIC_NAME,
      ExportRowDataObjKeys.MEASURE,
      ExportRowDataObjKeys.SEGMENT_LEVEL_1,
      ExportRowDataObjKeys.SEGMENT_LEVEL_2,
      ExportRowDataObjKeys.PERCENTAGE_OF_TOTAL_INDICATOR,
    ];
    let filteredColumns: ExportRowDataObjKeys[];
    if (segmentationLevel1.length === 0) {
      filteredColumns = allColumns.filter(
        (c) =>
          ![
            ExportRowDataObjKeys.SEGMENT_LEVEL_1,
            ExportRowDataObjKeys.SEGMENT_LEVEL_2,
            ExportRowDataObjKeys.PERCENTAGE_OF_TOTAL_INDICATOR,
          ].includes(c)
      );
    } else if (segmentationLevel2.length === 0) {
      filteredColumns = allColumns.filter(
        (c) => ![ExportRowDataObjKeys.SEGMENT_LEVEL_2].includes(c)
      );
    } else if (
      this.selectedSegmentLevel1Measure !==
        SegmentValTypes.PERCENTAGE_OF_TOTAL &&
      this.selectedSegmentLevel2Measure !== SegmentValTypes.PERCENTAGE_OF_TOTAL
    ) {
      filteredColumns = allColumns.filter(
        (c) => ![ExportRowDataObjKeys.PERCENTAGE_OF_TOTAL_INDICATOR].includes(c)
      );
    } else {
      filteredColumns = allColumns;
    }
    const dataColumns = [
      ...timeHeaderObjs,
      ...fixedHeaderObjs,
      ...benchmarkHeaderObjs,
    ];
    const dataForExport = exportRowDataObjs.map((r) =>
      [...filteredColumns, ...dataColumns.map((d) => d.value)].map((c) => r[c])
    );
    const headers = [
      ...filteredColumns.map((c) => getExportRowDataObjKeyTranslation(c)),
      ...dataColumns.map((d) => d.label),
    ];
    const translatedHeaders = headers;
    const exportData: ExportData = {
      data: dataForExport,
      headers: translatedHeaders,
    };
    const { formatDateApi } = dateManagerService.getInstance();
    const date = formatDateApi(getMomentDate());
    const fileName = `${exportPrefix.replace(/ /g, '_')}_${date}`;
    void exportStore.exportReport(fileName, exportData);
    this.toggleExportProcessing();
    loadingStateStore.loadFinished();
  };
}
